// Derives chart-ready shapes from a report's `summary` distributions and
// `rows`, since report payloads vary per category — this keeps
// ReportPreviewPage generic instead of hand-wiring charts per type.

const DATE_FIELD_CANDIDATES = ["date", "hiringDate", "startDate", "requestedDate", "timestamp"];

export const getDistributions = (summary = {}) =>
  Object.entries(summary)
    .filter(([, v]) => v && typeof v === "object" && !Array.isArray(v))
    .map(([key, value]) => ({
      key,
      data: Object.entries(value).map(([name, val]) => ({
        name,
        value: typeof val === "object" ? (val.count ?? 0) : Number(val) || 0,
      })).filter((d) => d.value > 0),
    }))
    .filter((d) => d.data.length > 0);

export const getSummaryCards = (summary = {}) =>
  Object.entries(summary)
    .filter(([, v]) => typeof v !== "object" || v === null)
    .map(([key, value]) => ({ key, value }));

const findDateField = (row) => DATE_FIELD_CANDIDATES.find((f) => row[f]);

/** Buckets rows by month (or day if the range is short) into a trend series. */
export const getTrendSeries = (rows = []) => {
  if (!rows.length) return [];
  const field = findDateField(rows[0]);
  if (!field) return [];

  const buckets = {};
  rows.forEach((r) => {
    const raw = r[field];
    if (!raw) return;
    // formatted values look like "23/07/2026" (en-GB) — parse defensively
    const parts = String(raw).split("/");
    const key = parts.length === 3 ? `${parts[2]}-${parts[1]}` : String(raw).slice(0, 7);
    buckets[key] = (buckets[key] || 0) + 1;
  });

  return Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => ({ name, value }));
};

/** Day-of-week x category grid, used for the Shift/Attendance heat map. */
export const getHeatMapData = (rows = [], xField, yField) => {
  if (!rows.length) return { data: [], xLabels: [], yLabels: [] };
  const xLabels = [...new Set(rows.map((r) => r[xField]).filter(Boolean))];
  const yLabels = [...new Set(rows.map((r) => r[yField]).filter(Boolean))].slice(0, 12);
  const counts = {};
  rows.forEach((r) => {
    const x = r[xField], y = r[yField];
    if (!x || !y || !yLabels.includes(y)) return;
    const k = `${x}|${y}`;
    counts[k] = (counts[k] || 0) + 1;
  });
  const data = Object.entries(counts).map(([k, value]) => {
    const [x, y] = k.split("|");
    return { x, y, value };
  });
  return { data, xLabels, yLabels };
};
